import { createSlice } from "@reduxjs/toolkit";
import { writeSharedAuthToken, writeSharedTenant } from "../../utils/sharedAuthCookie";
import { isSsoSession } from "auth/oidc";
import { hostSubdomainOf, resolveTenantSubdomain } from "config/tenancy";

// The `_subdomain` this app sends, handed on with the token so a sibling app on a
// host that is not an organization (devices) works in the same one.
const sentTenant = () =>
  resolveTenantSubdomain(hostSubdomainOf(window.location.hostname), localStorage.getItem("_subdomain"));

const initialState = {
  loading: false,
  isLoggedIn: true,
  authToken: null,
  error: null,
  user: null,
  data: null,
  // A sign-in waiting for its second step: { challenge, method: "sms" | "app",
  // destination, message }. The server holds the session until the code is right.
  twoFactor: null,
  _subdomain: null, // for overriding subdomain
  loginWithEmail: {
    data: null,
    error: null,
    loading: false,
  },
  handleUnAuthorized: {
    redirectToLogin: false,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginUserStart: (state) => {
      state.loading = true;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = null;
      state.user = null;
      state.data = null;
      state.twoFactor = null;
      state._subdomain = null;
    },
    twoFactorRequired: (state, action) => {
      state.loading = false;
      state.twoFactor = action.payload;
    },
    twoFactorCleared: (state) => {
      state.twoFactor = null;
    },
    loginUserSuccess: (state, action) => {
      state.loading = false;
      state.isLoggedIn = true;
      state.authToken = action.payload.accessToken;
      state.error = null;
      state.user = action.payload.userDetails;
      state.data = action.payload;
    },
    loginUserFailure: (state, action) => {
      state.loading = false;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = action.payload;
      state.user = null;
      state.data = null;
      state._subdomain = null;
    },
    logoutUserAction: (state, action) => {
      state.loading = false;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = null;
      state.user = null;
      state.data = null;
      state.twoFactor = null;
      state._subdomain = null;
    },
    tokenAction: (state, action) => {
      // A DIFFERENT access token (impersonation, org switch, magic link, a
      // new login) invalidates any stored refresh token: it belongs to the
      // previous session and would "renew" straight back into it. Paths that
      // issue a matching pair store the new refresh token right after this
      // (utils/session). Re-dispatching the same token — app start — keeps it.
      if (localStorage.getItem("authToken") !== action.payload) {
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("refreshTokenExpiresAt");
      }
      localStorage.setItem("authToken", action.payload);
      // Mirror to the parent domain so a sibling app on another host
      // (backoffice <-> devices) adopts this session instead of
      // showing its own login page. See utils/sharedAuthCookie. A single
      // sign-on session doesn't need it: every host asks Keycloak (auth/oidc).
      if (!isSsoSession()) writeSharedAuthToken(action.payload, sentTenant());
      state.authToken = action.payload;
    },
    overrideSubdomainAction: (state, action) => {
      localStorage.setItem("_subdomain", action.payload);
      if (!isSsoSession()) writeSharedTenant(state.authToken, sentTenant());
      state._subdomain = action.payload;
    },
    getUserStart: (state) => {
      // Refresh-in-flight: only flip loading. Do NOT clear user/data/
      // isLoggedIn — the React tree mid-mounted picker calls
      // `dispatch(getUser())` after saving a system pick, and nulling
      // state.user during that window made `pickSidebarVariant(null)`
      // unmount the sidebar in App.js, which let the catch-all route
      // bounce the user to /login before getUserSuccess could land.
      // Initial login still works fine: getUserSuccess populates user
      // from null on first load.
      state.loading = true;
      state.error = null;
    },
    getUserSuccess: (state, action) => {
      state.loading = false;
      state.isLoggedIn = true;
      state.error = null;
      state.data = action.payload; 
      state.user = action.payload.userDetails;
    },
    getUserFailure: (state, action) => {
      state.loading = false;
      state.isLoggedIn = false;
      state.authToken = null;
      state.error = action.payload;
      state.user = null;
      state.data = null;
      state._subdomain = null;
    },
    loginReset: (state, action) => {
      state.loading = false;
      state.error = null;
    },
    // My settings saved the user's own time zone (POST /timezones/user/add).
    // Patched into every copy of the user the app holds — login and
    // /auth/user-info nest it differently — so reports and timestamps switch
    // to it at once, without signing in again.
    userTimezoneUpdated: (state, action) => {
      const tz = action.payload || null;
      [
        state.user,
        state.user?.userDetails,
        state.user?.userProfile,
        state.data?.userDetails,
        state.data?.userDetails?.userDetails,
      ]
        .filter((u) => u && typeof u === "object")
        .forEach((u) => {
          u.timezone = tz;
        });
    },

    loginWithEmailStart: (state) => {
      state.loginWithEmail.loading = true;
      state.loginWithEmail.error = null;
      state.loginWithEmail.user = null;
    },
    loginWithEmailSuccess: (state, action) => {
      state.loginWithEmail.loading = false;
      state.loginWithEmail.error = null;
      state.loginWithEmail.data = action.payload;
    },
    loginWithEmailFailure: (state, action) => {
      state.loginWithEmail.loading = false;
      state.loginWithEmail.error = action.payload;
      state.loginWithEmail.data = null;
    },
    loginWithEmailReset: (state, action) => {
      state.loginWithEmail.loading = false;
      state.loginWithEmail.error = null;
      state.loginWithEmail.data = null;
    },

    handleUnAuthorizedAction: (state, action) => {
      state.handleUnAuthorized.redirectToLogin = action.payload;
    },
  },
});

export const {
  loginUserStart,
  loginUserSuccess,
  loginUserFailure,
  tokenAction,
  logoutUserAction,
  getUserStart,
  getUserSuccess,
  getUserFailure,
  loginReset,
  loginWithEmailStart,
  loginWithEmailSuccess,
  loginWithEmailFailure,
  loginWithEmailReset,
  handleUnAuthorizedAction,
  overrideSubdomainAction,
  twoFactorRequired,
  twoFactorCleared,
  userTimezoneUpdated,
} = authSlice.actions;

export default authSlice.reducer;
